import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { discoverySource, toMediaItem, tmdbImageUrl } from "../../sources/discoverySource";
import { useAmbientBackdrop } from "../../lib/pageBackdrop";
import { HubRow } from "../../components/media";
import { MediaCard } from "../../components/library/media-card";
import { PosterGrid } from "../../components/library/poster-grid";
import { LoadingState, NotFoundState } from "../../components/page-state";
import { LibraryCopiesRow } from "../../components/library/library-copies-row";
import { creditGuids, useLibraryCopies } from "../../lib/libraryCopies";

export const Route = createFileRoute("/_authed/discover/person/$personId")({
  component: PersonPage,
});

function PersonPage() {
  // An art route without art of its own: the shell's backdrop carries the
  // show you came from (lib/pageBackdrop) — first hook, before any early return.
  useAmbientBackdrop();
  const { personId } = Route.useParams();
  const [bioOpen, setBioOpen] = useState(false);

  const person = useQuery({
    queryKey: ["discover-person", personId],
    queryFn: () => discoverySource.getPerson(personId),
    staleTime: 6 * 60 * 60 * 1000,
  });
  // Which of the credits the connected servers hold — asked the moment the
  // person lands, before any early return (hooks order).
  const guids = creditGuids(person.data?.credits ?? []);
  const copies = useLibraryCopies(person.data?.id ?? null, guids);

  if (person.isLoading) {
    return <LoadingState />;
  }
  if (person.isError || !person.data) {
    return <NotFoundState backTo="/discover" backLabel="Back to Discover" />;
  }

  const p = person.data;
  // h632 is the largest sized TMDB profile cut (profiles don't ship poster
  // widths like w500) — right for the detail-scale 320px box.
  const photo = tmdbImageUrl(p.profilePath, "h632");
  const meta = [p.knownForDepartment || null, lifespan(p.birthday, p.deathday)]
    .filter(Boolean)
    .join(" · ");

  // The item detail view's composition, minus the backdrop: same cap and
  // 80px desktop gutter, same bar clearance, poster ladder and type scale.
  return (
    <div className="pb-16">
      <div className="rago-detail-cap">
        <div className="flex gap-12 px-6 pt-[calc(var(--rago-bar-h)+4rem)] pb-10 max-md:flex-col sm:px-10">
          <div className="w-[320px] shrink-0 max-xl:w-[280px] max-lg:w-[220px] max-md:w-[200px]">
            <div className="aspect-[2/3] overflow-hidden rounded-[var(--rago-radius-l)] border border-[var(--rago-line-2)] bg-[var(--rago-bg-2)] shadow-2xl">
              {photo ? (
                <img src={photo} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-4xl text-[var(--rago-text-3)]">
                  {p.name.slice(0, 1)}
                </span>
              )}
            </div>
          </div>

          <div className="min-w-0 max-w-[820px] flex-1">
            <h1 className="rago-display rago-rise mb-3.5 text-[clamp(2.6rem,1.8rem+3vw,4.25rem)] leading-[1.05]">
              {p.name}
            </h1>
            {meta ? (
              <p className="rago-rise rago-rise-1 mb-2 text-[15px] font-medium text-[var(--rago-text-1)]">
                {meta}
              </p>
            ) : null}
            {p.placeOfBirth ? (
              <p className="mb-6 text-sm text-[var(--rago-text-3)]">Born in {p.placeOfBirth}</p>
            ) : null}

            {p.biography ? (
              <div className="rago-rise rago-rise-2 max-w-[65ch]">
                <p
                  className={`text-base leading-relaxed font-medium text-[var(--rago-text-1)] ${
                    bioOpen ? "" : "line-clamp-5"
                  }`}
                >
                  {p.biography}
                </p>
                {p.biography.length > 400 ? (
                  <button
                    onClick={() => setBioOpen((v) => !v)}
                    className="mt-2 cursor-pointer text-sm text-[var(--rago-accent)] hover:text-[var(--rago-accent-strong)]"
                  >
                    {bioOpen ? "Show less" : "Read more"}
                  </button>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-[var(--rago-text-3)]">No biography available.</p>
            )}
          </div>
        </div>
      </div>

      <div className="rago-detail-below">
        <LibraryCopiesRow guids={guids} byGuid={copies.byGuid} />
        <HubRow
          hub={{ id: "known-for", title: "Known For", items: p.knownFor.map(toMediaItem) }}
          source={discoverySource}
        />

        {p.credits.length > 0 ? (
          <section className="px-6 sm:px-10">
            <h2 className="rago-display mb-4 text-[19px]">Filmography</h2>
            <PosterGrid>
              {p.credits.map((item) => (
                <MediaCard
                  key={`${item.mediaType}:${item.id}`}
                  item={toMediaItem(item)}
                  source={discoverySource}
                  // "2026 · Director, Writer" — the year and the person's
                  // roles on the title, Plex's filmography caption (the same
                  // line the Apple apps draw); a pre-roles broker leaves the
                  // type-derived caption.
                  caption={
                    item.roles?.length
                      ? [item.year, item.roles.join(", ")].filter(Boolean).join(" · ")
                      : undefined
                  }
                />
              ))}
            </PosterGrid>
          </section>
        ) : null}
      </div>
    </div>
  );
}

/** "1987–" / "1987–2020" / "b. 1987" style lifespan from ISO dates. */
function lifespan(birthday: string | null, deathday: string | null): string | null {
  if (!birthday) return null;
  const birthYear = birthday.slice(0, 4);
  if (deathday) return `${birthYear}–${deathday.slice(0, 4)}`;
  const age = ageFrom(birthday);
  return age != null ? `Born ${birthYear} · ${age} yrs` : `Born ${birthYear}`;
}

function ageFrom(birthday: string): number | null {
  const b = new Date(birthday);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age;
}
